import { avatarSrc } from "@/lib/defaults";
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useApp } from "@/context/AppContext";
import { localizeDict } from "@/lib/localText";
import { FollowSuggestions } from "@/components/FollowSuggestions";
import { InstallAppButton } from "@/components/InstallAppButton";
import {
  Home, Users, ShoppingBag, Radio,
  Film, Megaphone, CalendarDays, Bookmark,
  ChevronDown, ChevronUp, LifeBuoy, HeartHandshake, BookOpen, Clapperboard, Library,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { countryList } from "@/lib/countries";

const SIDEBAR_LINK_LABELS = {
  fr: { terms: "CGU", privacy: "Confidentialité", gdpr: "RGPD", child: "Sécurité des enfants", delete: "Supprimer mon compte", ticket: "Créer un ticket", faith: "Fait avec foi" },
  en: { terms: "Terms", privacy: "Privacy", gdpr: "GDPR", child: "Child safety", delete: "Account deletion", ticket: "Create a ticket", faith: "Made with faith" },
  es: { terms: "Términos", privacy: "Privacidad", gdpr: "RGPD", child: "Seguridad infantil", delete: "Eliminar cuenta", ticket: "Crear un ticket", faith: "Hecho con fe" },
  pt: { terms: "Termos", privacy: "Privacidade", gdpr: "RGPD", child: "Segurança infantil", delete: "Excluir conta", ticket: "Criar um ticket", faith: "Feito com fé" },
};

// Nav definitions per admin layout block key (zone: left_sidebar).
const NAV_DEFS = {
  nav_feed:        { to: "/home",        icon: Home,         tKey: "nav.feed",        fb: "Fil",             color: "#D97757" },
  nav_saved:       { to: "/saved",       icon: Bookmark,     tKey: "nav.saved",       fb: "Enregistrements", color: "#0D9488" },
  nav_reels:       { to: "/reels",       icon: Film,         tKey: "nav.reels",       fb: "Reels",           color: "#E0475F", feature: "reels" },
  nav_groups:      { to: "/groups",      icon: Users,        tKey: "nav.groups",      fb: "Groupes",         color: "#2E7CF6", feature: "groups" },
  nav_events:      { to: "/events",      icon: CalendarDays, tKey: "nav.events",      fb: "Événements",      color: "#8B5CF6" },
  nav_prayers:     { to: "/prayers",     icon: HeartHandshake, tKey: "nav.prayers",   fb: "Prières",         color: "var(--yalola-secondary)", feature: "prayers" },
  nav_testimonies: { to: "/testimonies", icon: HeartHandshake, tKey: "nav.testimonies", fb: "Témoignages",   color: "var(--yalola-secondary)", feature: "testimonies" },
  nav_reading:     { to: "/reading",     icon: BookOpen,     tKey: "nav.reading",     fb: "Plan de lecture", color: "#D97757", feature: "reading" },
  nav_marketplace: { to: "/marketplace", icon: ShoppingBag,  tKey: "nav.marketplace", fb: "Marketplace",     color: "#2D9E68", feature: "marketplace" },
  nav_ads:         { to: "/ads",         icon: Megaphone,    tKey: "nav.ads",         fb: "Publicités",      color: "#E0A63C", feature: "ads" },
  nav_live:        { to: "/live?create=1", icon: Radio,        tKey: "nav.live",        fb: "Live",            color: "#DC2626", feature: "live" },
};
const DEFAULT_NAV_ORDER = Object.keys(NAV_DEFS);

// Facebook-style flat menu, dynamically driven by the admin layout blocks.
export const LeftSidebar = ({ onNavigate }) => {
  const { user, t, config, lang, storiesUnseen } = useApp();
  const loc = useLocation();
  const features = config?.settings?.features || {};
  const isHost = ["preacher", "artist", "influencer"].includes(user?.role);
  const [showMore, setShowMore] = useState(false);
  const LBL = localizeDict(SIDEBAR_LINK_LABELS, lang, config?.translations, "sidebar_links");
  const legalCountry = (() => {
    const code = config?.settings?.country_of_legislation || "BE";
    const found = countryList(lang).find((c) => c.code === code);
    return found ? found.label : "Belgique";
  })();

  const sideBlocks = (config?.layout_blocks || []).filter((b) => b.zone === "left_sidebar");
  const blockOn = (key) => {
    const b = sideBlocks.find((x) => x.key === key);
    return !b || (b.visible !== false && b.enabled !== false);
  };

  const navBlocks = sideBlocks.filter((b) => NAV_DEFS[b.key]);
  let navKeys = navBlocks.length > 0
    ? navBlocks
        .filter((b) => b.key === "nav_feed" || (b.visible !== false && b.enabled !== false))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((b) => b.key)
    : [...DEFAULT_NAV_ORDER];
  // "Fil" is fixed: always present, always first.
  navKeys = ["nav_feed", ...navKeys.filter((k) => k !== "nav_feed")];
  // "Enregistrements" available by default even before the admin layout is re-seeded.
  if (!navKeys.includes("nav_saved") && !navBlocks.some((b) => b.key === "nav_saved")) {
    navKeys.splice(1, 0, "nav_saved");
  }
  // "Prières" available by default even before the admin layout is re-seeded.
  if (!navKeys.includes("nav_prayers") && !navBlocks.some((b) => b.key === "nav_prayers")) {
    navKeys.push("nav_prayers");
  }
  // "Témoignages" & "Plan de lecture" available by default even before the admin layout is re-seeded.
  if (!navKeys.includes("nav_testimonies") && !navBlocks.some((b) => b.key === "nav_testimonies")) {
    navKeys.push("nav_testimonies");
  }
  if (!navKeys.includes("nav_reading") && !navBlocks.some((b) => b.key === "nav_reading")) {
    navKeys.push("nav_reading");
  }

  const items = navKeys
    .map((k) => ({ key: k, ...NAV_DEFS[k] }))
    .filter((it) => !it.feature || features[it.feature] !== false);

  // Facebook-style: show N items, hide the rest behind "See more" (admin-configurable).
  const maxItems = Math.max(1, Number(config?.settings?.nav_max_items) || 7);
  const hasMore = items.length > maxItems;
  const visibleItems = hasMore && !showMore ? items.slice(0, maxItems) : items;

  return (
    <nav data-testid="left-sidebar">
      {user && blockOn("profile_card") && (
        <>
          <Link
            to={`/profile/${user.id}`}
            className="flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
            data-testid="left-profile-card"
            onClick={onNavigate}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={avatarSrc(user)} />
              <AvatarFallback style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", fontWeight: 700 }}>{user.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-semibold text-[14px] truncate flex items-center gap-1" style={{ color: "var(--yalola-text)" }}>
                {user.name}
                {user.is_verified && <VerifiedBadge user={user} size={13} />}
              </div>
              <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="left-profile-subtitle">{t("nav.my_profile", "Mon profil")}</div>
            </div>
          </Link>
          <div className="my-3 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="left-profile-divider" />
        </>
      )}

      <div data-testid="nav-menu-block">
        {visibleItems.map(({ key, to, icon: Icon, tKey, fb, color }) => {
          const path = to.split("?")[0];
          const active = loc.pathname === path || (path !== "/home" && loc.pathname.startsWith(path));
          return (
            <Link
              key={key}
              to={to}
              onClick={onNavigate}
              className="flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
              style={{ background: active ? "rgba(0,0,0,0.06)" : "transparent" }}
              data-testid={`nav-${path.slice(1)}`}
            >
              <span className="h-9 w-9 rounded-full grid place-items-center shrink-0 relative" style={{ background: color + "1f" }}>
                <Icon size={18} style={{ color }} strokeWidth={2.1} />
                {key === "nav_feed" && storiesUnseen > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full grid place-items-center text-[9px] font-bold text-white"
                    style={{ background: "var(--yalola-primary)", boxShadow: "0 0 0 2px var(--yalola-surface)" }}
                    title={`${storiesUnseen} ${t("story.unseen_title", "stories non vues")}`}
                    data-testid="nav-feed-unseen-badge"
                  >
                    {storiesUnseen > 9 ? "9+" : storiesUnseen}
                  </span>
                )}
              </span>
              <span className="text-[14px]" style={{ color: "var(--yalola-text)", fontWeight: active ? 700 : 500 }}>{t(tKey, fb)}</span>
            </Link>
          );
        })}
        {hasMore && (
          <button
            onClick={() => setShowMore((v) => !v)}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
            data-testid="nav-see-more"
          >
            <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(0,0,0,0.08)" }}>
              {showMore ? <ChevronUp size={18} strokeWidth={2.1} /> : <ChevronDown size={18} strokeWidth={2.1} />}
            </span>
            <span className="text-[14px] font-medium" style={{ color: "var(--yalola-text)" }}>
              {showMore ? t("nav.see_less", "Voir moins") : t("nav.see_more", "Voir plus")}
            </span>
          </button>
        )}
        {isHost && (
          <Link
            to="/mes-directs"
            onClick={onNavigate}
            className="flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
            style={{ background: loc.pathname === "/mes-directs" ? "rgba(0,0,0,0.06)" : "transparent" }}
            data-testid="nav-my-lives"
          >
            <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "#DC26261f" }}>
              <Clapperboard size={18} color="#DC2626" strokeWidth={2.1} />
            </span>
            <span className="text-[14px]" style={{ color: "var(--yalola-text)", fontWeight: loc.pathname === "/mes-directs" ? 700 : 500 }}>{t("menu.my_lives", "Mes directs")}</span>
          </Link>
        )}
        {features.resources === true && (
          <Link
            to="/ressources"
            onClick={onNavigate}
            className="flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
            style={{ background: loc.pathname.startsWith("/ressources") ? "rgba(0,0,0,0.06)" : "transparent" }}
            data-testid="nav-resources"
          >
            <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "#0F76761f" }}>
              <Library size={18} color="#0F7676" strokeWidth={2.1} />
            </span>
            <span className="text-[14px]" style={{ color: "var(--yalola-text)", fontWeight: loc.pathname.startsWith("/ressources") ? 700 : 500 }}>{t("nav.resources", "Ressources")}</span>
          </Link>
        )}
        <InstallAppButton variant="sidebar" />
      </div>

      {blockOn("follow_suggestions") && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <FollowSuggestions variant="sidebar" />
        </div>
      )}

      {/* Visiteur : le grand pied de page du site (SiteFooter) porte déjà ces liens → un seul pied de page pour les robots. */}
      {user && (
      <div className="mt-3 pt-3 border-t text-[12px]" style={{ borderColor: "rgba(0,0,0,0.08)", color: "var(--yalola-text-muted)" }} data-testid="sidebar-legal-links">
        <Link
          to="/support"
          onClick={onNavigate}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg font-semibold transition-colors hover:bg-[rgba(0,0,0,0.05)]"
          style={{ color: "var(--yalola-primary)" }}
          data-testid="sidebar-link-ticket"
        >
          <LifeBuoy size={15} strokeWidth={2.2} /> {LBL.ticket}
        </Link>
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1.5 px-2">
          <Link to="/legal/terms" onClick={onNavigate} className="hover:underline" data-testid="sidebar-link-terms">{LBL.terms}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/privacy" onClick={onNavigate} className="hover:underline" data-testid="sidebar-link-privacy">{LBL.privacy}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/gdpr" onClick={onNavigate} className="hover:underline" data-testid="sidebar-link-gdpr">{LBL.gdpr}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/child-safety" onClick={onNavigate} className="hover:underline" data-testid="sidebar-link-child-safety">{LBL.child}</Link>
          <span aria-hidden>·</span>
          <Link to="/supprimer-mon-compte" onClick={onNavigate} className="hover:underline" data-testid="sidebar-link-delete">{LBL.delete}</Link>
        </div>
        <div className="mt-2 px-2 leading-snug">© {new Date().getFullYear()} Yalola · {legalCountry} · RGPD · {LBL.faith}</div>
      </div>
      )}
    </nav>
  );
};
